/**
 * AppIntegrationsTab — the per-app Integrations tab content (#1154,
 * Slice 2 of the three-tier integrations reshape meta #1152;
 * drawer-ified in #1184, Slice 1 of meta #1183).
 *
 * Mounts under the Apps page's tab strip alongside Overview / Variants
 * / Reference Docs / Candidate Tests. Two cards stacked:
 *   1. GitHub binding — repository picker + per-variant trigger-rule
 *      oversight. Each variant row is a click target that opens a
 *      right-side drawer with that variant's trigger rules; the
 *      operator never leaves the App configuration context. The
 *      previously-supported "Default branch for new rules" text input
 *      and the "Post Check Run on failure (app default)" toggle both
 *      retired in #1184 (see `IAppGitHubBinding` for the rationale).
 *   2. Slack binding — default channel picker + event subscription
 *      checkboxes + per-variant notification-override oversight.
 *      Variant rows here open a separate drawer for that variant's
 *      Slack notification overrides — the surface that previously
 *      lived on the deleted variant Automation page.
 *
 * **URL state for the drawer.** The drawer's open/closed state is
 * driven by a `?variantConfig=<vid>` query param that composes with
 * the existing `?tab=integrations` convention. Opening the drawer
 * sets the param; closing strips it. Reloading a page with the param
 * re-opens the drawer for that variant. The page back-button strips
 * the param and closes the drawer.
 *
 * **Disabled-picker empty state.** When the workspace has no GitHub
 * (or Slack) connection, the picker renders disabled with copy
 * pointing the operator at Workspace Settings → Integrations to
 * install the provider.
 *
 * **Partial-update semantics.** Each card's Save button commits ONLY
 * that provider's binding. The PATCH endpoint accepts a
 * `{ github?, slack? }` partial — saving GitHub doesn't blow away the
 * Slack state and vice versa. Per-card busy state + per-card error
 * banner so a transient GitHub failure doesn't disable the Slack
 * card.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronRight, ExternalLink, Loader2, Github, MessageSquare, Workflow } from 'lucide-react'
import {
  Button,
  Label,
  Badge,
  SearchableSelect,
} from '@/components/ui'
import {
  applicationsAPI,
  type AppIntegrationsResponse,
} from '@/lib/api'
import { AppIssueSourceCard } from './AppIssueSourceCard'
import { useAppContext } from '@/hooks/useAppContext'
import {
  APP_SLACK_EVENT_SUBSCRIPTIONS,
  type AppGitHubBinding,
  type AppSlackBinding,
  type AppSlackEventSubscription,
} from '@/types'
import { VariantTriggerRulesDrawer } from '@/pages/AppIntegrationsTab/VariantTriggerRulesDrawer'
import { VariantNotificationOverridesDrawer } from '@/pages/AppIntegrationsTab/VariantNotificationOverridesDrawer'
import { VariantPipelineIntegrationDrawer } from '@/pages/AppIntegrationsTab/VariantPipelineIntegrationDrawer'

/** Customer-facing labels for each Slack event the app default can
 * subscribe to. Plain English on the left of the wire value, so the
 * checkbox list reads naturally to non-engineer operators. */
const SLACK_EVENT_LABELS: Record<
  AppSlackEventSubscription,
  { label: string; help?: string }
> = {
  'run.failed': {
    label: 'On failure',
    help: 'When a run completes with failing tests.',
  },
  'run.completed': {
    label: 'On completion',
    help: 'Every run, including healthy ones. Noisy on stable variants.',
  },
  'run.errored': {
    label: 'On error',
    help: 'When a run crashes before reporting tests (infra failure).',
  },
  'run.cancelled': {
    label: 'On cancellation',
    help: 'When a run is cancelled manually or by a queue trip.',
  },
}

interface AppIntegrationsTabProps {
  appId: string
  /** Bumps whenever the parent wants this tab to re-fetch its data
   * — e.g. after a variant is renamed elsewhere on the page so the
   * oversight rows pick up the new label. The tab also re-fetches
   * after its own save actions. */
  refreshKey?: number
}

export function AppIntegrationsTab({ appId, refreshKey = 0 }: AppIntegrationsTabProps) {
  const { workspaceId, allVariants } = useAppContext()
  // Drawer state is URL-managed. `?variantConfig=<vid>` (composing
  // with the existing `?tab=integrations`) drives which variant's
  // drawer is open; absence of the param means no drawer. The handler
  // pair below mutates the URL and lets React Router's setSearchParams
  // propagate the change back through the hook — a single source of
  // truth between the drawer state and the URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const openVariantConfigId = searchParams.get('variantConfig')
  // The drawer needs to know which oversight surface the open variant
  // came from — the same `variantConfig=<id>` param drives all three
  // drawers (GitHub trigger rules, Slack notifications, Pipeline
  // Integration); a sibling `variantConfigKind=trigger|slack|pipeline`
  // discriminates. Sanitize against the known enum so a crafted URL
  // (`variantConfigKind=foo`) can't strand `?variantConfig=...` with no
  // drawer rendered (and no close affordance) — unknown values fall
  // back to `'trigger'`, the more common entry point. Defaults to
  // `'trigger'` when missing for backwards compatibility with any
  // pre-#1184 hand-written URLs.
  //
  // The `'pipeline'` value was added in #1191 (Slice 2 of meta #1189)
  // when the Pipeline Integration card landed as a third sibling on
  // this tab.
  const rawKind = searchParams.get('variantConfigKind')
  const openVariantConfigKind: 'trigger' | 'slack' | 'pipeline' =
    rawKind === 'slack'
      ? 'slack'
      : rawKind === 'pipeline'
        ? 'pipeline'
        : 'trigger'

  function openDrawer(
    variantId: string,
    kind: 'trigger' | 'slack' | 'pipeline',
  ) {
    const params = new URLSearchParams(searchParams)
    params.set('variantConfig', variantId)
    params.set('variantConfigKind', kind)
    setSearchParams(params, { replace: false })
  }
  function closeDrawer() {
    const params = new URLSearchParams(searchParams)
    params.delete('variantConfig')
    params.delete('variantConfigKind')
    setSearchParams(params, { replace: false })
  }

  const [data, setData] = useState<AppIntegrationsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // One workspace-scoped Integrations settings URL, shared by all three
  // "Open Workspace Settings" links on this tab (GitHub, Slack, Issue
  // tracker) so they can't drift. Built from the active workspace id so the
  // click lands on the right workspace's settings — the GitHub/Slack links
  // were once bare `?tab=...` hrefs that only mutated the Applications page's
  // tab query (Copilot review on #1160), and the issue-tracker card later
  // hardcoded a relative `../../settings/integrations` that dropped the
  // workspace entirely (#2306).
  const workspaceIntegrationsSettingsUrl = `/workspaces/${workspaceId}/settings?tab=workspace.integrations`
  // Per-provider save state — independent so a transient GitHub
  // failure doesn't visually freeze the Slack card.
  const [savingGitHub, setSavingGitHub] = useState(false)
  const [savingSlack, setSavingSlack] = useState(false)
  const [githubSaveError, setGitHubSaveError] = useState<string | null>(null)
  const [slackSaveError, setSlackSaveError] = useState<string | null>(null)
  const [githubSaveOk, setGitHubSaveOk] = useState(false)
  const [slackSaveOk, setSlackSaveOk] = useState(false)

  // Form state initialized from the fetched bindings; the user mutates
  // these and clicks Save to commit. Tracked separately from `data`
  // so the form doesn't snap back on every refresh.
  const [githubForm, setGitHubForm] = useState<AppGitHubBinding>({})
  const [slackForm, setSlackForm] = useState<AppSlackBinding>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    applicationsAPI
      .getIntegrations(appId)
      .then((res) => {
        if (cancelled) return
        setData(res)
        setGitHubForm(res.bindings.github ?? {})
        setSlackForm(res.bindings.slack ?? {})
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appId, refreshKey])

  // ── Picker option lists (rendered to the SearchableSelect input) ──
  const githubRepoOptions = useMemo(() => {
    if (!data || !data.github.connected) return []
    return data.github.repos.map((r) => ({
      value: r.repoIdentifier,
      label: r.repoIdentifier,
    }))
  }, [data])

  // Variants belonging to this app — sourced from the app context's
  // workspace-wide variant list. The Pipeline Integration card uses
  // this to render a per-variant row list, identical in shape to
  // the GitHub / Slack oversight rows.
  const pipelineCardVariants = useMemo(
    () =>
      allVariants
        .filter((v) => v.applicationId === appId)
        .map((v) => ({ variantId: v.id, variantName: v.name })),
    [allVariants, appId],
  )

  const slackChannelOptions = useMemo(() => {
    if (!data || !data.slack.connected) return []
    return data.slack.channels.map((c) => ({
      value: c.id,
      // Prefix `#` (public) or 🔒 (private) so the operator can tell
      // the channel type at a glance — Slack's own UI uses this
      // convention. `name` already excludes the prefix.
      label: c.isPrivate ? `🔒 ${c.name}` : `# ${c.name}`,
    }))
  }, [data])

  async function saveGitHub() {
    setSavingGitHub(true)
    setGitHubSaveError(null)
    setGitHubSaveOk(false)
    try {
      const res = await applicationsAPI.updateIntegrations(appId, {
        // Send the whole subdoc — partial-field semantics are nice in
        // theory but the form is small enough that whole-subdoc
        // replacement on save is simpler and matches what the user
        // sees ("save this card's values").
        github: githubForm,
      })
      setData((prev) =>
        prev
          ? {
              ...prev,
              bindings: { ...prev.bindings, github: res.bindings.github },
            }
          : prev,
      )
      setGitHubSaveOk(true)
    } catch (err) {
      setGitHubSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingGitHub(false)
    }
  }

  async function saveSlack() {
    setSavingSlack(true)
    setSlackSaveError(null)
    setSlackSaveOk(false)
    try {
      const res = await applicationsAPI.updateIntegrations(appId, {
        slack: slackForm,
      })
      setData((prev) =>
        prev
          ? {
              ...prev,
              bindings: { ...prev.bindings, slack: res.bindings.slack },
            }
          : prev,
      )
      setSlackSaveOk(true)
    } catch (err) {
      setSlackSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingSlack(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading integrations…
      </div>
    )
  }
  if (loadError || !data) {
    return (
      <div className="p-4 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive">
        Failed to load integrations: {loadError ?? 'unknown error'}
      </div>
    )
  }

  const githubConnected = data.github.connected
  const slackConnected = data.slack.connected

  return (
    <div className="space-y-4">
      {/* Issue-tracker binding card (#1558 Slice 1b) — the app's inbound
          requirement/defect source. Self-contained; consumes the
          issueSource binding + connectable trackers off the same
          getIntegrations response. */}
      <AppIssueSourceCard
        appId={appId}
        initialBinding={data.bindings.issueSource}
        connections={data.issueTracker.connections}
        settingsUrl={workspaceIntegrationsSettingsUrl}
      />

      {/* GitHub binding card */}
      <section
        aria-labelledby="github-binding-heading"
        className="rounded-lg border bg-card p-4"
      >
        <header className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-[#1f2328] text-white">
            <Github className="h-4 w-4" />
          </div>
          <h3
            id="github-binding-heading"
            className="text-sm font-semibold flex-1"
          >
            GitHub repository
          </h3>
          {githubConnected ? (
            data.bindings.github?.repoIdentifier ? (
              <Badge variant="default">Linked</Badge>
            ) : (
              <Badge variant="outline">Not linked</Badge>
            )
          ) : (
            // Name the provider explicitly so the badge doesn't read
            // as "your OttoTester workspace isn't connected" — the
            // workspace IS connected, GitHub specifically isn't.
            <Badge variant="outline">GitHub not connected</Badge>
          )}
        </header>

        {!githubConnected ? (
          <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
            <strong className="block text-foreground mb-1">
              GitHub isn’t connected on this workspace yet.
            </strong>
            Connect GitHub on the workspace to bind a repo to this app.{' '}
            <Link
              to={workspaceIntegrationsSettingsUrl}
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              Open Workspace Settings <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              Pulls from the workspace’s GitHub install. To change which GitHub
              org or installation backs this workspace, go to Workspace
              Settings → Integrations.
            </p>

            {data.github.connected && data.github.error && (
              <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200">
                {data.github.error} You can still keep the current selection.
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="github-repo-picker">Repository</Label>
                <SearchableSelect
                  id="github-repo-picker"
                  value={githubForm.repoIdentifier ?? ''}
                  onChange={(v) =>
                    setGitHubForm((prev) => ({
                      ...prev,
                      repoIdentifier: v === '' ? undefined : v,
                    }))
                  }
                  options={githubRepoOptions}
                  placeholder={
                    githubRepoOptions.length === 0
                      ? 'No repositories available'
                      : 'Select a repository'
                  }
                  searchPlaceholder="Search repos…"
                  disabled={githubRepoOptions.length === 0}
                  aria-label="GitHub repository"
                />
                <p className="text-[11px] text-muted-foreground">
                  Which repo events this app’s variants react to.
                </p>
              </div>

              {/*
               * #1184 (Slice 1 of meta #1183) trimmed two fields that
               * lived here previously:
               *   - "Default branch for new rules" text input — was
               *     leftover from the push / pull_request trigger era
               *     and has no role now that deployment_status is the
               *     only TriggerRule event type (#1173).
               *   - "Post Check Run on failure (app default)" toggle —
               *     Check Run posting is now a system-on default;
               *     disable-able per Replay template in the template
               *     editor, which already exists today.
               * No replacement UI at this layer — both behaviors moved
               * elsewhere as noted.
               */}
            </div>

            <div className="mt-4 flex items-center gap-3">
              {/* Save is enabled even when the picker has no options.
                  When the workspace's GitHub repo list failed to load
                  (transient API blip), the customer can still update
                  the default branch / toggle without picking a new
                  repo — the PATCH route accepts a partial subdoc.
                  Disabling the entire form on a picker-side blip
                  trapped the operator (Copilot review on #1160). */}
              <Button
                onClick={saveGitHub}
                disabled={savingGitHub}
                size="sm"
              >
                {savingGitHub && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Save
              </Button>
              {githubSaveError && (
                <span className="text-xs text-destructive">
                  {githubSaveError}
                </span>
              )}
              {githubSaveOk && !githubSaveError && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  Saved.
                </span>
              )}
            </div>
          </>
        )}

        {/* Oversight: variants with trigger rules for this app's repo.
            Suppressed entirely when GitHub isn't connected upstream —
            there's nothing meaningful to show until the workspace has
            a provider connection. */}
        {githubConnected && (
          <OversightSection
            title="Trigger rules per variant"
            rows={data.oversight.github}
            emptyHint="No variants yet."
            countNoun="rule"
            countNounPlural="rules"
            emptyRowCopy="no rules yet"
            onRowClick={(variantId) => openDrawer(variantId, 'trigger')}
          />
        )}
      </section>

      {/* Pipeline Integration card — pull-style invocation (#1191,
        * Slice 2 of meta #1189). Sits BETWEEN GitHub (push trigger
        * above) and Slack (notifications below) so the configuration
        * flow reads top-to-bottom: deployment_status triggers →
        * pipeline triggers → notifications.
        *
        * Unlike GitHub / Slack, this card has no workspace-level
        * connection prerequisite — the customer's CI calls OttoTester
        * with a personal access token they generate themselves. The
        * card surfaces the per-variant drawer behind a "Configure"
        * affordance for each variant in the app. */}
      <PipelineIntegrationCard
        variants={pipelineCardVariants}
        onConfigureVariant={(variantId) => openDrawer(variantId, 'pipeline')}
      />

      {/* Slack binding card */}
      <section
        aria-labelledby="slack-binding-heading"
        className="rounded-lg border bg-card p-4"
      >
        <header className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-[#4a154b] text-white">
            <MessageSquare className="h-4 w-4" />
          </div>
          <h3
            id="slack-binding-heading"
            className="text-sm font-semibold flex-1"
          >
            Slack channel
          </h3>
          {slackConnected ? (
            data.bindings.slack?.defaultChannelId ? (
              <Badge variant="default">Linked</Badge>
            ) : (
              <Badge variant="outline">Not linked</Badge>
            )
          ) : (
            // Name the provider explicitly so the badge doesn't read
            // as "your OttoTester workspace isn't connected" — the
            // workspace IS connected, Slack specifically isn't.
            <Badge variant="outline">Slack not connected</Badge>
          )}
        </header>

        {!slackConnected ? (
          <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
            <strong className="block text-foreground mb-1">
              Slack isn’t connected on this workspace yet.
            </strong>
            Connect Slack on the workspace to bind a default channel to this
            app.{' '}
            <Link
              to={workspaceIntegrationsSettingsUrl}
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              Open Workspace Settings <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              Pulls from the workspace’s Slack install
              {data.slack.connected && data.slack.teamName ? (
                <>
                  {' '}
                  (<code>{data.slack.teamName}</code>)
                </>
              ) : null}
              . Variants can override per event.
            </p>

            {data.slack.connected && data.slack.error && (
              <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200">
                {data.slack.error} You can still keep the current selection.
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="slack-channel-picker">Default channel</Label>
                <SearchableSelect
                  id="slack-channel-picker"
                  value={slackForm.defaultChannelId ?? ''}
                  onChange={(v) =>
                    setSlackForm((prev) => ({
                      ...prev,
                      defaultChannelId: v === '' ? undefined : v,
                    }))
                  }
                  options={slackChannelOptions}
                  placeholder={
                    slackChannelOptions.length === 0
                      ? 'No channels available'
                      : 'Select a channel'
                  }
                  searchPlaceholder="Search channels…"
                  disabled={slackChannelOptions.length === 0}
                  aria-label="Slack default channel"
                />
                <p className="text-[11px] text-muted-foreground">
                  Where this app’s notifications go by default. Variants can
                  override per event.
                </p>
              </div>

              <div className="space-y-1">
                <Label>Notify on</Label>
                <ul className="space-y-1.5">
                  {APP_SLACK_EVENT_SUBSCRIPTIONS.map((ev) => {
                    const checked = (
                      slackForm.defaultEventSubscriptions ?? []
                    ).includes(ev)
                    const meta = SLACK_EVENT_LABELS[ev]
                    return (
                      <li key={ev} className="flex items-start gap-2">
                        <input
                          id={`slack-event-${ev}`}
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={(e) =>
                            setSlackForm((prev) => {
                              const current = prev.defaultEventSubscriptions ?? []
                              const next = e.target.checked
                                ? Array.from(new Set([...current, ev]))
                                : current.filter((x) => x !== ev)
                              return {
                                ...prev,
                                defaultEventSubscriptions: next,
                              }
                            })
                          }
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`slack-event-${ev}`}
                            className="cursor-pointer"
                          >
                            {meta.label}{' '}
                            <code className="text-[10px] text-muted-foreground font-mono">
                              {ev}
                            </code>
                          </Label>
                          {meta.help && (
                            <p className="text-[11px] text-muted-foreground">
                              {meta.help}
                            </p>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
                <p className="text-[11px] text-muted-foreground">
                  Override-able per variant.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              {/* See the GitHub Save above for the rationale — Save
                  stays enabled even when the channel picker has no
                  options so the customer can still adjust the event
                  subscriptions on a transient channel-list blip. */}
              <Button
                onClick={saveSlack}
                disabled={savingSlack}
                size="sm"
              >
                {savingSlack && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Save
              </Button>
              {slackSaveError && (
                <span className="text-xs text-destructive">{slackSaveError}</span>
              )}
              {slackSaveOk && !slackSaveError && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  Saved.
                </span>
              )}
            </div>
          </>
        )}

        {/* Oversight: variants with channel overrides for this app.
            Suppressed entirely when Slack isn't connected upstream. */}
        {slackConnected && (
          <OversightSection
            title="Notification overrides per variant"
            rows={data.oversight.slack}
            emptyHint="No variants yet."
            countNoun="override"
            countNounPlural="overrides"
            emptyRowCopy="no overrides yet"
            onRowClick={(variantId) => openDrawer(variantId, 'slack')}
          />
        )}
      </section>

      {/* ── Drawers (URL-state-managed) ───────────────────────────────
        * One open drawer at a time; `variantConfigKind` discriminates
        * which provider's drawer renders. The drawer components own
        * their own list + inline editor; this page only mounts them
        * and supplies the open/close handle. */}
      {/* No connection, no rules and no settings for them (#2175 slice 4).
          The link that opens this drawer is already behind the same gate;
          this stops a stale URL reopening it after a disconnect. */}
      {openVariantConfigId && openVariantConfigKind === 'trigger' && githubConnected && (
        <VariantTriggerRulesDrawer
          appId={appId}
          variantId={openVariantConfigId}
          githubRepoIdentifier={data.bindings.github?.repoIdentifier}
          onClose={closeDrawer}
        />
      )}
      {openVariantConfigId && openVariantConfigKind === 'slack' && (
        <VariantNotificationOverridesDrawer
          appId={appId}
          variantId={openVariantConfigId}
          onClose={closeDrawer}
        />
      )}
      {openVariantConfigId && openVariantConfigKind === 'pipeline' && (
        <VariantPipelineIntegrationDrawer
          appId={appId}
          variantId={openVariantConfigId}
          onClose={closeDrawer}
        />
      )}
    </div>
  )
}

// ── OversightSection ────────────────────────────────────────────────
//
// Per-variant oversight rows ("Trigger rules per variant" on the
// GitHub card, "Notification overrides per variant" on the Slack
// card). Each row is a click target that opens a per-variant drawer
// in the App Integrations tab (#1184 — replaced the previous
// `<Link to="...automation">` navigation; the standalone Automation
// page retired in the same slice).
//
// Row affordance: button-styled row + chevron. The whole row is
// clickable; no separate CTA button — matches the pre-#1167 simplification
// argument (single destination, single affordance, clean scan).
interface OversightSectionProps {
  title: string
  rows: { variantId: string; variantName: string; count: number }[]
  /** Copy when the app has zero variants at all (different from "all
   * variants have count === 0" — the latter renders rows). */
  emptyHint: string
  /** Customer-facing noun for the count. GitHub side: "rule" / "rules".
   * Slack side: "override" / "overrides". Per #1160's per-provider
   * vocabulary fix. */
  countNoun: string
  countNounPlural: string
  /** Copy for the count=0 row, mirroring the noun. */
  emptyRowCopy: string
  /** Fires when the operator clicks a row. Opens the appropriate
   * drawer; the URL-state update lives in the caller (#1184). */
  onRowClick: (variantId: string) => void
}

function OversightSection({
  title,
  rows,
  emptyHint,
  countNoun,
  countNounPlural,
  emptyRowCopy,
  onRowClick,
}: OversightSectionProps) {
  return (
    <div className="mt-4 pt-4 border-t">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const muted = r.count === 0
            const statusText = muted
              ? emptyRowCopy
              : `${r.count} ${r.count === 1 ? countNoun : countNounPlural}`
            return (
              <li key={r.variantId}>
                <button
                  type="button"
                  onClick={() => onRowClick(r.variantId)}
                  className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-left transition-colors hover:bg-muted hover:border-border-strong group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Open ${r.variantName} configuration drawer`}
                  data-variant-id={r.variantId}
                  data-testid="oversight-variant-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {r.variantName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {statusText}
                    </div>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ── PipelineIntegrationCard ─────────────────────────────────────────
//
// Sits between the GitHub binding card and the Slack binding card —
// the second of three sibling cards on the App Integrations tab
// after #1191 (Slice 2 of meta #1189). Renders the customer-facing
// pitch for the pull-style integration story (the half that
// complements deployment_status push triggers) and a per-variant row
// list whose click opens the Pipeline Integration drawer.
//
// **No workspace-connection prerequisite.** Unlike the GitHub and
// Slack cards, this card always renders enabled — there is no
// workspace integration to install. The token the snippet uses is a
// personal access token the customer generates themselves on the
// Account Settings page, deep-linked from inside the drawer.
//
// **Variant rows mirror the oversight pattern.** Same affordance the
// GitHub and Slack cards use for their per-variant drawers (#1184),
// so the operator's mental model of "click a variant row, drawer
// slides in" stays consistent across the three cards.
interface PipelineIntegrationCardProps {
  variants: { variantId: string; variantName: string }[]
  onConfigureVariant: (variantId: string) => void
}

function PipelineIntegrationCard({
  variants,
  onConfigureVariant,
}: PipelineIntegrationCardProps) {
  return (
    <section
      aria-labelledby="pipeline-integration-heading"
      className="rounded-lg border bg-card p-4"
      data-testid="pipeline-integration-card"
    >
      <header className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-to-br from-primary to-purple-600 text-white">
          <Workflow className="h-4 w-4" />
        </div>
        <h3
          id="pipeline-integration-heading"
          className="text-sm font-semibold flex-1"
        >
          Pipeline Integration
        </h3>
        <Badge variant="outline">Available</Badge>
      </header>

      <p className="text-xs text-muted-foreground mb-3">
        Run OttoTester from your own CI — GitHub Actions, GitLab,
        CircleCI, Jenkins, anything that can hit an HTTP API. Useful
        when your deploy platform doesn't post{' '}
        <code className="font-mono text-[11px] bg-muted/60 px-1 rounded">
          deployment_status
        </code>{' '}
        events, or when you want OttoTester to gate a release before
        the deploy itself.
      </p>

      {variants.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No variants yet. Create a variant first, then come back here to
          generate a pipeline snippet for it.
        </p>
      ) : (
        <div className="mt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Configure workflow per variant
          </p>
          <ul className="space-y-2">
            {variants.map((v) => (
              <li key={v.variantId}>
                <button
                  type="button"
                  onClick={() => onConfigureVariant(v.variantId)}
                  className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-left transition-colors hover:bg-muted hover:border-border-strong group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Configure pipeline workflow for ${v.variantName}`}
                  data-variant-id={v.variantId}
                  data-testid="pipeline-variant-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {v.variantName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Configure workflow
                    </div>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
